import React, { useState } from 'react'
import { useTranslation } from '../i18n/LanguageContext'
import { browserPlanStorage, PLAN_EVENT, planIdentity, planFingerprint,
  planPayload, readPlanDraft, restorePlanDraft, portablePlanInputs, writePlanDraft } from '../lib/planDrafts.js'
import './PlanDrafts.css'

export const notifyPlansChanged = () => window.dispatchEvent(new Event(PLAN_EVENT))
export const PLAN_ERRORS = {
  identity: 'Saving requires an exact foreign listing identity and published observation. You can still download your inputs.',
  unavailable: 'Browser storage is unavailable or full. Your inputs are still on this page; download them before leaving.',
  invalid: 'A saved plan could not be read. It has not been overwritten. Your inputs are still on this page.',
  conflict: 'This plan was changed in another tab. Download your inputs before reloading; the other version has not been overwritten.',
  limit: 'You have 100 saved plans on this browser. Export and remove an old plan before saving another.',
  size: 'This comparison is too large to save safely on this browser. Download your inputs instead; existing plans are unchanged.',
}

export function usePlanDraft(car, kind, context = '') {
  const identity = planIdentity(car, kind), fingerprint = planFingerprint(car, kind, context)
  const [initial] = useState(() => {
    const read = identity ? readPlanDraft(browserPlanStorage(), identity.key) : { draft: null, error: 'identity' }
    return { ...read, ...restorePlanDraft(read.draft, car, kind, context) }
  })
  const [payload, setPayload] = useState(initial.payload)
  const [saved, setSaved] = useState(initial.draft)
  const [error, setError] = useState(initial.error)
  const [reviewed, setReviewed] = useState(initial.matches || !initial.draft)
  const dirty = !saved || JSON.stringify(planPayload(kind, payload)) !== JSON.stringify(planPayload(kind, saved.payload))
  const needsReview = Boolean(saved && !reviewed)
  function save() {
    const result = writePlanDraft(browserPlanStorage(), { car, kind, context, payload, expectedRevision: saved?.revision ?? null })
    setError(result.error)
    if (result.draft) { setSaved(result.draft); setReviewed(true); notifyPlansChanged() }
  }
  function reuse() { setPayload(portablePlanInputs(saved, car, kind)); setReviewed(true) }
  function download() {
    const data = { schema: 'automargin-plan-export.v1', exported_at: new Date().toISOString(), identity,
      published_observation: car.scraped_at || null, decision_snapshot_id: car.decision_snapshot_id || null,
      payload: planPayload(kind, payload), prior_saved_draft: saved,
      limitation: 'Personal assumptions, not current source evidence, verified quotes or a buying approval.' }
    const url = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' }))
    const link = document.createElement('a'); link.href = url
    link.download = `automargin-plan-${kind}-${identity?.country_code || 'unknown'}-${identity?.listing_id || 'unknown'}.json`
    link.click(); setTimeout(() => URL.revokeObjectURL(url), 1000)
  }
  return { payload, setPayload, saved, error, dirty, needsReview, save, reuse, download, canSave: Boolean(fingerprint) }
}

export default function PlanDraftControls({ draft, saveLabel = 'Save plan on this device', updateLabel = 'Update saved plan' }) {
  const { t } = useTranslation()
  return <div className="plan-draft-controls">
    {draft.needsReview && <p role="status">{t('The published observation changed or this saved review is over a day old. Saved inputs are not current quotes or completed checks.')}
      <button type="button" onClick={draft.reuse}>{t('Reuse my saved assumptions and recheck quotes')}</button></p>}
    <div className="plan-draft-actions">
      <button type="button" disabled={!draft.canSave || (!draft.dirty && !draft.needsReview)} onClick={draft.save}>{t(draft.saved ? updateLabel : saveLabel)}</button>
      <button type="button" className="plan-secondary" onClick={draft.download}>{t('Download inputs, including blanks')}</button>
    </div>
    <small role="status">{t(draft.saved ? draft.dirty ? 'Unsaved changes. Use Update saved plan to keep them.' : 'Saved on this browser. Find it under Saved.' : 'Optional: keep these inputs on this browser, not in an account. Other people using this browser can read them.')}</small>
    {!draft.canSave && !draft.error && <small>{t(PLAN_ERRORS.identity)}</small>}
    {draft.error && <p role="alert">{t(PLAN_ERRORS[draft.error])}</p>}
  </div>
}
